<script setup lang="ts">
  import { computed, resolveComponent } from 'vue'
  import type { ButtonProps } from '../../types/button'

  import Icon from '../icons/Icon.vue'

  const props = defineProps<ButtonProps>()

  defineEmits<{
    click: [mouseEvent: MouseEvent]
  }>()

  const component = computed(() => {
    return props.to ? resolveComponent('NuxtLink') : 'button'
  })
</script>

<template>
  <component
    :is="component"
    class="ds-button"
    :class="{ small, block, link: to, 'fit-content': fitContent, loading }"
    :disabled="disabled || loading"
    :target
    :to
    @click="$emit('click', $event)"
  >
    <Icon
      v-if="loading"
      name="loading"
      class="ds-button--loader"
    />
    <slot name="icon">
      <Icon
        v-if="icon"
        :name="icon"
        class="ds-button--icon"
        :class="{ small, loading }"
      />
    </slot>
    <span
      class="ds-button--label text-medium"
      :class="{
        'text-body-sm': small,
        'text-body-base': !small,
        loading
      }"
    >
      <slot />
    </span>
  </component>
</template>

<style scoped lang="postcss">
  $text-color: #fff;

  .ds-button {
    position: relative;
    display: inline-flex;
    box-sizing: border-box;
    padding: var(--spacing-sm) var(--spacing-md);
    justify-content: center;
    align-items: center;
    gap: var(--spacing-xs);

    border: none;
    border-radius: var(--radius-semirounded);
    background-blend-mode: overlay, normal;

    transition: background-color 300ms linear;

    &.loading {
      .ds-button--icon,
      .ds-button--label,
      :slotted(svg) {
        visibility: hidden;
      }
    }

    &.link {
      text-decoration: none;
      &:visited {
        color: inherit;
      }
    }

    &.small {
      padding: var(--spacing-xs) var(--spacing-sm);
      gap: var(--spacing-xxs);

      :slotted(svg) {
        width: 10px;
        height: 10px;
      }
    }

    &.block {
      width: 100%;
    }

    &.fit-content {
      width: fit-content;
    }

    &--loader {
      position: absolute;
      color: $text-color;
    }

    &--icon {
      width: 14px;
      height: 14px;
      color: $text-color;
      aspect-ratio: 1/1;
      flex-shrink: 0;

      &.small {
        width: 10px;
        height: 10px;
      }
    }

    :slotted(svg) {
      width: 14px;
      height: 14px;
      color: $text-color;
      aspect-ratio: 1/1;
      flex-shrink: 0;
      color: $text-color;
    }

    &--label {
      color: $text-color;
      text-align: center;
    }

    &:hover,
    &:focus {
      background-blend-mode: overlay, normal;
    }

    &:disabled {
      box-shadow: none;
    }
  }
</style>
